import React from 'react';

/**
 * Self-contained SVG pie / donut chart.
 *
 * Mirrors the `Highcharts.chart('my_task_container', { type: 'pie' })`
 * widgets used on the original Django dashboards
 * (`templates/task_new/index.html`, `complaints_management_system/dashboard.html`)
 * — but without pulling in the ~600KB Highcharts bundle.
 *
 * Props:
 *   title       — heading shown above the chart
 *   data        — array of { name, value, color? }; entries with value=0 are
 *                 still rendered in the legend but contribute no slice.
 *   size        — outer SVG size in px (default 220)
 *   donutRatio  — inner radius as a fraction of outer (0 = pie, 0.55 = donut)
 *
 * Colours default to the original Taskose `.task-status.type-N` palette so
 * dashboards look consistent with the rest of the React app.
 */

// Same palette used elsewhere (status badges, dashboards, etc.).
const DEFAULT_COLOURS = [
  '#7980ff', // primary indigo
  '#5fd788', // success green
  '#34c8de', // info cyan
  '#fcdb66', // warning yellow
  '#f9771d', // orange
  '#ec4899', // pink
  '#f05a5a', // danger red
  '#778899', // slate
  '#1b325f', // dark navy
];

/** Convert (cx, cy, r, angleDeg) → cartesian {x, y}. 0° = 12 o'clock. */
function polar(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Build an SVG `d` attribute for a single donut slice. */
function slicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const o1 = polar(cx, cy, rOuter, startAngle);
  const o2 = polar(cx, cy, rOuter, endAngle);
  const i1 = polar(cx, cy, rInner, endAngle);
  const i2 = polar(cx, cy, rInner, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  if (rInner <= 0) {
    return [
      `M ${cx} ${cy}`,
      `L ${o1.x} ${o1.y}`,
      `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${o2.x} ${o2.y}`,
      'Z',
    ].join(' ');
  }
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${o2.x} ${o2.y}`,
    `L ${i1.x} ${i1.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${i2.x} ${i2.y}`,
    'Z',
  ].join(' ');
}

export default function PieChart({
  title,
  data = [],
  size = 220,
  donutRatio = 0.55,
}) {
  const total = data.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 6;          // 6px padding so the SVG never clips
  const rInner = rOuter * donutRatio;

  // Layout: chart on the left, legend on the right (stacks below on mobile).
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {title && (
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700 }}>
          {title}
        </h3>
      )}

      {total === 0 ? (
        <div className="ty-empty" style={{ width: '100%' }}>
          No data to chart yet.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={title || 'pie chart'}
          >
            {(() => {
              let cursor = 0;
              return data
                .filter((d) => Number(d.value) > 0)
                .map((d, idx) => {
                  const value = Number(d.value);
                  const fraction = value / total;
                  const start = cursor;
                  const end = cursor + fraction * 360;
                  cursor = end;
                  const colour = d.color || DEFAULT_COLOURS[idx % DEFAULT_COLOURS.length];
                  // Edge case: a single 100% slice — draw a full ring instead
                  // of an arc with start === end (which would render nothing).
                  if (fraction === 1) {
                    return (
                      <g key={d.name}>
                        <circle cx={cx} cy={cy} r={rOuter} fill={colour} />
                        {rInner > 0 && (
                          <circle cx={cx} cy={cy} r={rInner} fill="var(--ty-surface)" />
                        )}
                      </g>
                    );
                  }
                  return (
                    <path
                      key={d.name}
                      d={slicePath(cx, cy, rOuter, rInner, start, end)}
                      fill={colour}
                      stroke="var(--ty-surface)"
                      strokeWidth={2}
                    >
                      <title>
                        {d.name}: {value} ({Math.round(fraction * 100)}%)
                      </title>
                    </path>
                  );
                });
            })()}

            {/* Centre total when rendered as a donut. */}
            {donutRatio > 0 && (
              <g style={{ pointerEvents: 'none' }}>
                <text
                  x={cx}
                  y={cy - 4}
                  textAnchor="middle"
                  style={{ fontSize: 22, fontWeight: 800, fill: 'var(--ty-text)' }}
                >
                  {total}
                </text>
                <text
                  x={cx}
                  y={cy + 14}
                  textAnchor="middle"
                  style={{ fontSize: 11, fill: 'var(--ty-muted)', letterSpacing: '0.04em' }}
                >
                  TOTAL
                </text>
              </g>
            )}
          </svg>

          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gap: 6,
              fontSize: 13,
              minWidth: 140,
            }}
          >
            {data.map((d, idx) => {
              const colour = d.color || DEFAULT_COLOURS[idx % DEFAULT_COLOURS.length];
              const value = Number(d.value) || 0;
              const pct = total ? Math.round((value / total) * 100) : 0;
              return (
                <li
                  key={d.name}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: colour,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, color: 'var(--ty-text)' }}>{d.name}</span>
                  <span style={{ color: 'var(--ty-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {value} ({pct}%)
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
